import { segmentTrack } from '@/hooks/analytics/useTracking';

import type { CIConfigExpertActionTypes } from './CIConfigExpert';

// Avo tracking plan for the AI config assistant (CI config expert). The standard/pinned fields
// (initiator, is_known_user, tracking_type, event_scope, …) come from segmentTrack's baseProperties,
// and workspace_slug is filled in by segmentTrack from the current account, so only event-specific
// properties are set here; event_type is only overridden where the plan pins something other than
// the 'interaction' default. platform, however, is pinned on the opt-in popup events but is not part
// of the base properties, so those send it explicitly.

// The dialog shell reports its close routes (X, ESC) indistinguishably, so both land on
// 'close_button'; only the Cancel/Dismiss button identifies itself.
export type CIConfigExpertOptInDismissMethod = 'close_button' | 'cancel_button';

type Ctx = {
  appSlug?: string;
};

const commonProps = ({ appSlug }: Ctx) => ({
  app_slug: appSlug,
  ai_assistant_type: 'ai_config_assistant',
});

type ConversationCtx = Ctx & {
  conversationId?: string;
  action?: CIConfigExpertActionTypes;
};

const conversationProps = ({ conversationId, action, ...ctx }: ConversationCtx) => ({
  ...commonProps(ctx),
  ai_assistant_conversation_id: conversationId,
  ai_assistant_action: action,
});

// `source` values are owned by the entry points (the Workflow Editor's AI buttons send theirs over in
// the OPEN_CI_CONFIG_EXPERT payload), so it stays a plain string here.
type OptInPopupCtx = Ctx & {
  source?: string;
  action?: CIConfigExpertActionTypes;
};

const optInPopupProps = ({ source, action, ...ctx }: OptInPopupCtx) => ({
  ...commonProps(ctx),
  source,
  ai_assistant_action: action ?? 'general_ask_ai',
  platform: 'website',
});

export function trackOptInPopupShown({ canOptIn, ...ctx }: OptInPopupCtx & { canOptIn: boolean }): void {
  segmentTrack('AI Assistant Opt In Popup Shown', {
    ...optInPopupProps(ctx),
    has_right_to_enable_ai_assistant: canOptIn,
  });
}

export function trackOptInPopupDismissed({
  canOptIn,
  method,
  ...ctx
}: OptInPopupCtx & { canOptIn: boolean; method: CIConfigExpertOptInDismissMethod }): void {
  segmentTrack('AI Assistant Opt In Popup Dismissed', {
    ...optInPopupProps(ctx),
    has_right_to_enable_ai_assistant: canOptIn,
    popup_step_dismiss_method: method,
  });
}

export function trackOptInAttempted({ canOptIn, ...ctx }: OptInPopupCtx & { canOptIn: boolean }): void {
  segmentTrack('AI Assistant Opt In Attempted', {
    ...optInPopupProps(ctx),
    has_right_to_enable_ai_assistant: canOptIn,
    event_type: 'product',
  });
}

export function trackDocumentationOpened({ canOptIn, ...ctx }: OptInPopupCtx & { canOptIn: boolean }): void {
  segmentTrack('AI Assistant Documentation Opened', {
    ...optInPopupProps({ ...ctx, source: 'ai_assistant_opt_in_popup' }),
    has_right_to_enable_ai_assistant: canOptIn,
  });
}

export function trackAssistantOpened({
  source,
  isResumed,
  priorTurnCount,
  ...ctx
}: ConversationCtx & { source?: string; isResumed: boolean; priorTurnCount: number }): void {
  segmentTrack('AI Assistant Opened', {
    ...conversationProps(ctx),
    source,
    is_resumed: isResumed,
    prior_turn_count: priorTurnCount,
  });
}

export function trackPromptSubmitted({
  turnIndex,
  isFirstPrompt,
  promptCharCount,
  ...ctx
}: ConversationCtx & { turnIndex: number; isFirstPrompt: boolean; promptCharCount: number }): void {
  segmentTrack('AI Assistant Prompt Submitted', {
    ...conversationProps(ctx),
    turn_index: turnIndex,
    is_first_prompt: isFirstPrompt,
    prompt_char_count: promptCharCount,
  });
}

export function trackResponseReceived({
  turnIndex,
  responseTimeMs,
  hasResponsePayload,
  ...ctx
}: ConversationCtx & { turnIndex: number; responseTimeMs: number; hasResponsePayload?: boolean }): void {
  segmentTrack('AI Assistant Response Received', {
    ...conversationProps(ctx),
    turn_index: turnIndex,
    response_time_ms: responseTimeMs,
    has_response_payload: hasResponsePayload,
  });
}

export function trackChangesApplied({ turnCount, ...ctx }: ConversationCtx & { turnCount: number }): void {
  segmentTrack('AI Assistant Changes Applied', {
    ...conversationProps(ctx),
    turn_count: turnCount,
  });
}

export function trackChatCleared({ turnCount, ...ctx }: ConversationCtx & { turnCount: number }): void {
  segmentTrack('AI Assistant Chat Cleared', {
    ...conversationProps(ctx),
    turn_count: turnCount,
  });
}

export function trackSessionDismissed({
  turnCount,
  sessionDurationS,
  ...ctx
}: ConversationCtx & { turnCount: number; sessionDurationS: number }): void {
  segmentTrack('AI Assistant Session Dismissed', {
    ...conversationProps(ctx),
    turn_count: turnCount,
    session_duration_s: sessionDurationS,
  });
}

export function trackPromptingGuideOpened({ turnCount, ...ctx }: ConversationCtx & { turnCount: number }): void {
  segmentTrack('AI Prompting Guide Opened', {
    ...conversationProps(ctx),
    turn_count: turnCount,
  });
}

export function trackResponseRated({
  reactionType,
  turnIndex,
  hasResponsePayload,
  ...ctx
}: ConversationCtx & { reactionType: string; turnIndex: number; hasResponsePayload?: boolean }): void {
  segmentTrack('AI Assistant Response Rated', {
    ...conversationProps(ctx),
    reaction_type: reactionType,
    turn_index: turnIndex,
    has_response_payload: hasResponsePayload,
  });
}
